// Track when Formidable Forms validation errors occur

import * as $ from 'jquery';

// Type definitions for Formidable Forms events
declare global {
	interface Window {
		gtag: any;
	}
}

interface FrmFormResponse {
	errors?: Record<string, string>;
}

/**
 * Count invalid fields in the form
 */
function countInvalidFields($form: JQuery): number {
	return $form.find('input[aria-invalid="true"], select[aria-invalid="true"], textarea[aria-invalid="true"], .frm_error input, .frm_error select, .frm_error textarea').length;
}

/**
 * Get a more descriptive field name by looking for labels, placeholders, etc.
 */
function getDescriptiveFieldName($field: JQuery, originalName: string): string {
	// Try to find a label for this field
	const fieldId = $field.attr('id');
	if (fieldId) {
		const $label = $field.closest('form').find(`label[for="${fieldId}"]`);
		if ($label.length) {
			const labelText = $label.text().trim();
			if (labelText) {
				return labelText;
			}
		}
	}
	
	// Look for label in the same field container
	const $fieldContainer = $field.closest('.frm_form_field, .frm_field_container');
	const $containerLabel = $fieldContainer.find('label, .frm_primary_label, .frm_label');
	if ($containerLabel.length) {
		const labelText = $containerLabel.first().text().trim();
		if (labelText) {
			return labelText;
		}
	}
	
	// Try placeholder text
	const placeholder = $field.attr('placeholder');
	if (placeholder) {
		return placeholder;
	}
	
	// Try aria-label
	const ariaLabel = $field.attr('aria-label');
	if (ariaLabel) {
		return ariaLabel;
	}
	
	// Fall back to original name but make it more readable
	if (originalName.includes('item_meta[')) {
		const fieldType = $field.attr('type') || $field.prop('tagName').toLowerCase();
		return `${fieldType}_field_${originalName}`;
	}
	
	return originalName || 'unnamed_field';
}

/**
 * Extract error message for a field
 */
function getFieldErrorMessage($field: JQuery, fieldName: string, responseErrors: Record<string, string>): string {
	// Check response errors first
	if (responseErrors[fieldName]) {
		return responseErrors[fieldName];
	}
	
	// Look for error message in field container
	const $fieldContainer = $field.closest('.frm_form_field, .frm_field_container');
	const $errorMessage = $fieldContainer.find('.frm_error, .error-message, .field-error');
	if ($errorMessage.length) {
		return $errorMessage.first().text().trim();
	}
	
	return 'Validation failed';
}

/**
 * Handles form-wide validation errors
 * Triggered when form submission fails with validation errors
 */
function onFormErrors(event: JQuery.TriggeredEvent, form: HTMLElement, response: FrmFormResponse) {
	// Check if gtag is available before proceeding
	if (typeof window.gtag === 'undefined') {
		return;
	}

	const $form = $(form);
	// get the current page title and use that for the form name 
	const pageTitle = document.title;
	const readableFormName = pageTitle;
	const formId = $form.attr("id") || 'unknown_form_id';
	const errorCount = countInvalidFields($form);

	window.gtag('event', 'form_fail', {
		form_id: formId,
		form_name: readableFormName,
		error_count: errorCount
	});
}

/**
 * Handles individual field validation errors
 * Triggered when a specific field fails validation
 */
function onFieldError(event: JQuery.TriggeredEvent, fieldContainer: JQuery, fieldKey: string, jsErrors: any) {
	// Check if gtag is available before proceeding
	if (typeof window.gtag === 'undefined') {
		return;
	}

	const $fieldContainer = $(fieldContainer);
	const fieldInput = $fieldContainer.find('input, select, textarea').first();
	const originalFieldName = fieldInput.attr('name') || fieldInput.attr('id') || 'unnamed';
	const descriptiveFieldName = getDescriptiveFieldName(fieldInput, originalFieldName);
	const $form = $fieldContainer.closest('form');
	// get the current page title and use that for the form name 
	const pageTitle = document.title;
	const readableFormName = pageTitle;
	const formId = $form.attr("id") || 'unknown_form_id';
	const errorMessage = getFieldErrorMessage(fieldInput, originalFieldName, { [fieldKey]: jsErrors });

	window.gtag('event', 'form_field_error', {
		form_id: formId,
		form_name: readableFormName,
		field_name: descriptiveFieldName,
		error_type: errorMessage,
	});
}

/**
 * Listen for Formidable Forms validation errors
 */
$(document).on("frmFormErrors", onFormErrors);
$(document).on("frmAddFieldError", onFieldError);
